import React from "react";
import { useAuth } from "@/lib/auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, User as UserIcon, ChevronDown, Menu as MenuIcon } from "lucide-react";

export default function Topbar({ onOpenMobileNav }) {
  const { user, logout } = useAuth();
  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header
      className="h-16 bg-white border-b border-slate-200 sticky top-0 z-30 flex items-center justify-between px-3 sm:px-6"
      data-testid="topbar"
    >
      <div className="flex items-center gap-2 sm:gap-4">
        {/* Mobile hamburger — opens the sidebar drawer */}
        <button
          type="button"
          onClick={onOpenMobileNav}
          className="md:hidden -ml-1 p-2 rounded-md text-slate-700 hover:bg-slate-100 active:bg-slate-200"
          aria-label="Open navigation"
          data-testid="mobile-nav-open-btn"
        >
          <MenuIcon className="h-5 w-5" />
        </button>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-400 font-mono hidden sm:block">
          Xentra · CRM Workspace
        </div>
        <div className="font-heading font-bold text-slate-900 sm:hidden">Xentra</div>
      </div>

      <div className="flex items-center gap-3">
        <div className="hidden md:block text-sm text-slate-500">
          <span className="text-slate-400">Signed in as</span>{" "}
          <span className="text-slate-900 font-medium">{user?.email}</span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-btn"
              aria-label="Open user menu"
              className="flex items-center gap-2 rounded-full hover:bg-slate-100 px-1.5 py-1 transition-colors border border-transparent hover:border-slate-200"
            >
              <Avatar className="h-8 w-8 border border-slate-200">
                {user?.picture && <AvatarImage src={user.picture} alt={user.name} />}
                <AvatarFallback className="bg-blue-600 text-white text-xs font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <ChevronDown className="h-4 w-4 text-slate-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel>
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
              <div className="mt-2 inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                {user?.role || "user"}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled className="gap-2">
              <UserIcon className="h-4 w-4" /> Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="logout-button"
              onClick={logout}
              className="gap-2 text-red-600 focus:text-red-700"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Always-visible quick logout button as a fallback for clarity & testability */}
        <button
          data-testid="logout-btn"
          onClick={logout}
          title="Sign out"
          className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-slate-600 hover:bg-red-50 hover:text-red-700 border border-slate-200 hover:border-red-200 transition-colors"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </header>
  );
}
